import { h } from 'preact';
import { useCallback, useRef, useState } from 'preact/hooks';
import { useDispatch, useSelector } from 'react-redux';

import { Translate } from '@travel/translation';
import { useDeviceTypes, useDialogHandler } from '@travel/traveler-core/hooks';
import { getSearchRatParams } from '@travel/traveler-core/utils/jrdpHelper';
import isNotEmptyArray from '@travel/ui/utils/arrays';
import cx from '@travel/ui/utils/classnames';
import { stringify } from '@travel/utils';

import Breadcrumb from 'components/BreadCrumb';
import DataLayer from 'components/DataLayer';
import EmptyState from 'components/EmptyState';
import HotelMap from 'components/HotelMap';
import ProviderListSearchHeader from 'components/ProviderListSearchHeader';
import {
  getHotels,
  getHotelListMapBreadcrumb,
  getIsFetching as getIsFetchingHotelList,
} from 'store/hotelList/selectors';
import { getHotelLocations, getIsFetchingMapList } from 'store/hotelListMap/selectors';
import List from './components/List';

import { pushLocation } from 'store/__router/actions';

import paths from 'constants/paths';
import {
  getDataLayersArrayParams,
  getPlaceLayers,
  getValidAccommodationsPath,
  BreadcrumbType,
} from 'utils/search';

import styles from './hotelListMap.module.scss';

import type { URLParams } from '../HotelList/resolver';
import type { URLSearchQuery } from 'Common-Types';
import type { HotelMapItem } from 'HotelListMap-Types';
export type Props = {
  path: string;
  url?: string;
  params: URLParams;
  query?: URLSearchQuery;
};

const getDataLayersArrayParamsForSP = (providers: Array<HotelMapItem>) =>
  providers.reduce((result, proObj) => {
    return {
      providerIds: (result['providerIds'] ? `${result['providerIds']},` : '') + proObj.id,
    };
  }, {});

const HotelListMap = (props: Props) => {
  const {
    params: { pageIdentifier, pageNumber },
    query,
  } = props;
  const { isPC } = useDeviceTypes();
  const dispatch = useDispatch();
  const breadcrumbs = useSelector(getHotelListMapBreadcrumb);
  const isFetchingHotelList = useSelector(getIsFetchingHotelList);
  const isFetchingMapList = useSelector(getIsFetchingMapList);
  const hotels = useSelector(getHotels);
  const hotelLocations = useSelector(getHotelLocations);

  const mapRef = useRef<google.maps.Map | null>(null);
  const accommodationAreaPath =
    hotels?.accommodationArea?.pathId ?? hotelLocations?.accommodationArea?.pathId;

  const accommodations = hotels.accommodations ?? hotelLocations.accommodations;
  const accommodationsPath = getValidAccommodationsPath(accommodations);
  const accommodationsOrSearchKey = accommodationsPath ? accommodationsPath : accommodationAreaPath;
  const isProviderValid = isNotEmptyArray(hotels.providers);
  const isProviderLocationValid = isNotEmptyArray(hotelLocations.providers);

  const { isOpen: isMaximizedMap, onToggle: toggleMaximizedMapView } = useDialogHandler(
    query?.maximizedMap === 'true',
  );
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [hoveredProvider, setHoveredProvider] = useState<string | null>(null);

  const dataLayersArrayParams = isPC
    ? isProviderValid
      ? getDataLayersArrayParams(hotels.providers)
      : {}
    : isProviderLocationValid
    ? getDataLayersArrayParamsForSP(hotelLocations?.providers)
    : {};

  const setMapInstance = useCallback(mapObj => (mapRef.current = mapObj), []);
  const placeLayers = getPlaceLayers(breadcrumbs, query, BreadcrumbType.HOTEL_LIST_MAP);

  const onMapPinClick = useCallback((providerId?: string) => {
    setSelectedProvider(providerId || null);
  }, []);

  const searchByArea = useCallback(
    (lat?: number, lng?: number, zoomLevel?: number) => {
      dispatch(
        pushLocation({
          pathname: paths.providerListMap.pathResolver(pageIdentifier),
          search: stringify({
            ...props.query,
            latitude: lat ?? props.query?.latitude,
            longitude: lng ?? props.query?.longitude,
            zoomLevel: zoomLevel ? zoomLevel : props.query?.zoomLevel,
            maximizedMap: isMaximizedMap,
          }),
        }),
      );
    },
    [dispatch, pageIdentifier, props.query, isMaximizedMap],
  );

  const switchView = useCallback(
    (shouldGoToHotelListUrl?: boolean) => {
      dispatch(
        pushLocation({
          pathname: (shouldGoToHotelListUrl
            ? paths.providerList
            : paths.providerListMap
          ).pathResolver(pageIdentifier),
          search: stringify({
            ...props.query,
            // to keep the list view rely on active search condition only
            // (to prevent a situation that list view cannot modify the current coordinate)
            ...(shouldGoToHotelListUrl && {
              latitude: undefined,
              longitude: undefined,
              zoomLevel: undefined,
            }),
          }),
        }),
      );
    },
    [dispatch, pageIdentifier, props.query],
  );

  const onHoverProviderCard = useCallback(
    (providerId: string) => setHoveredProvider(providerId || null),
    [],
  );

  return (
    <>
      {!isPC ? (
        <>
          {!isFetchingMapList ? (
            <HotelMap
              hotels={hotelLocations}
              setMapInstance={setMapInstance}
              searchQuery={query}
              searchByArea={searchByArea}
              switchView={switchView}
              selectedHotel={selectedProvider}
              onMapPinClick={onMapPinClick}
              isFetching={isFetchingMapList}
              pathId={pageIdentifier}
              accommodations={accommodations}
            />
          ) : (
            <div class={styles.mapSkeleton} />
          )}
        </>
      ) : (
        <>
          <Breadcrumb placeLayers={placeLayers} className={styles.breadcrumb} />
          <ProviderListSearchHeader
            query={props.query}
            pathName={paths.providerListMap.pathResolver(pageIdentifier)}
            totalCount={hotels.total}
            isHotelListMap
          />
          <div className={cx(styles.splitStyle, !isProviderValid && styles.noItem)}>
            {isProviderValid ||
            isProviderLocationValid ||
            isFetchingHotelList ||
            isFetchingMapList ? (
              <>
                {!isMaximizedMap && (
                  <List
                    hotels={hotels}
                    pageIdentifier={pageIdentifier}
                    urlSearchQuery={props.query}
                    accommodationAreaPath={accommodationAreaPath}
                    accommodations={accommodations}
                    onHoverProviderCard={onHoverProviderCard}
                    pageNumber={pageNumber}
                  />
                )}

                <div
                  data-testid="hotelListMap-mapContainer"
                  className={cx(styles.mapWrapper, isMaximizedMap && styles.mapMaximized)}
                >
                  {!isFetchingMapList ? (
                    <HotelMap
                      setMapInstance={setMapInstance}
                      searchByArea={searchByArea}
                      switchView={switchView}
                      onClickMaximizeMap={toggleMaximizedMapView}
                      isMaximizedMap={isMaximizedMap}
                      hotels={hotelLocations}
                      searchQuery={query}
                      selectedHotel={selectedProvider}
                      hoveredHotel={hoveredProvider}
                      onMapPinClick={onMapPinClick}
                      isFetching={isFetchingMapList}
                      pathId={pageIdentifier}
                      accommodations={accommodations}
                    />
                  ) : (
                    <div class={styles.mapSkeleton} />
                  )}
                </div>
              </>
            ) : (
              <section className={styles.mapList}>
                <EmptyState
                  title={<Translate id="Hotel_List.No_Results.Sorry_Message" />}
                  description={<Translate id="Hotel_List.No_Results.Change_Condition" />}
                />
              </section>
            )}
          </div>
        </>
      )}
      {hotels.accommodationArea && (
        <DataLayer
          page="hotel_list_map"
          {...getSearchRatParams(query)}
          {...dataLayersArrayParams}
          searchKey={accommodationsOrSearchKey}
          destinationArea={accommodationsOrSearchKey}
          isBookable={isProviderValid ? 'bookable' : 'unbookable'}
          stepParam="step1"
        />
      )}
    </>
  );
};

export default HotelListMap;
